// apps/accounts/src/pages/admin/StaffSalary.jsx
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { DollarSign, RefreshCw, Check } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

export default function StaffSalary() {
  const [staffList, setStaffList] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedStaff, setSelectedStaff] = useState('');

  useEffect(() => { fetchStaff(); }, []);

  const fetchStaff = async () => {
    try {
      const staff = await pb.collection('site_employees').getFullList({ expand: 'user', filter: 'isActive = true' });
      setStaffList(staff);
    } catch (err) { toast.error('Failed to load staff'); }
  };

  const fetchSalaries = async () => {
    if (!selectedStaff) { toast.info('Select a staff member'); return; }
    setLoading(true);
    try {
      const data = await pb.collection('staff_salary').getFullList({ filter: `staff = "${selectedStaff}" && month = "${month}"`, expand: 'staff,staff.user', sort: '-created' });
      setSalaries(data);
    } catch (err) { toast.error('Failed to load salary data'); } finally { setLoading(false); }
  };

  const calculateAndGenerate = async () => {
    if (!selectedStaff) { toast.error('Select a staff member'); return; }
    setLoading(true);
    try {
      const staff = await pb.collection('site_employees').getOne(selectedStaff);
      const basic = staff.salary || 0;
      const [year, mon] = month.split('-');
      const start = `${year}-${mon}-01`;
      const end = `${year}-${mon}-30`;
      const absents = await pb.collection('staff_attendance').getFullList({
        filter: `staff = "${selectedStaff}" && date >= "${start}" && date <= "${end}" && status = "absent"`,
        expand: 'leave_request',
      });
      let deductibleDays = 0;
      for (const rec of absents) {
        const leave = rec.expand?.leave_request;
        if (leave && leave.is_paid === true) continue;
        deductibleDays++;
      }
      const dailySalary = basic / 30;
      const deduction = deductibleDays * dailySalary;
      const advances = await pb.collection('staff_advance_payments').getFullList({ filter: `staff = "${selectedStaff}" && status = "pending"` });
      const totalAdvance = advances.reduce((sum, a) => sum + a.amount, 0);
      const netPayable = Math.max(0, basic - deduction - totalAdvance);
      await pb.collection('staff_salary').create({ staff: selectedStaff, month, basic_salary: basic, allowances: 0, deductions: deduction, advance_deduction: totalAdvance, net_payable: netPayable, paid: false });
      toast.success(`Salary generated: ?${netPayable}`);
      fetchSalaries();
    } catch (err) { toast.error('Generation failed: ' + err.message); } finally { setLoading(false); }
  };

  const markPaid = async (salaryId) => {
    try { await pb.collection('staff_salary').update(salaryId, { paid: true, paid_date: new Date().toISOString() }); toast.success('Marked as paid'); fetchSalaries(); } 
    catch (err) { toast.error('Failed to update'); }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <h1 className="text-2xl font-bold">Salary Management</h1>
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Staff</label>
              <select value={selectedStaff} onChange={(e) => setSelectedStaff(e.target.value)} className="w-full px-3 py-2 border rounded-lg bg-white">
                <option value="">Select Staff</option>
                {staffList.map(s => <option key={s.id} value={s.id}>{s.expand?.user?.name || s.id}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Month</label>
              <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-full" />
            </div>
            <div><Button variant="outline" onClick={fetchSalaries} className="w-full"><RefreshCw className="w-4 h-4 mr-1" /> View</Button></div>
            <div><Button onClick={calculateAndGenerate} className="w-full bg-amber-500 hover:bg-amber-600 text-white" disabled={loading}><DollarSign className="w-4 h-4 mr-1" /> Generate Salary</Button></div>
          </div>
        </CardContent>
      </Card>
      {selectedStaff && (
        <div className="space-y-3">
          {loading && <p className="text-center text-slate-500">Processing...</p>}
          {!loading && salaries.length === 0 && <Card><CardContent className="p-8 text-center text-slate-500">No salary record for this month. Click "Generate Salary".</CardContent></Card>}
          {salaries.map(s => (
            <Card key={s.id} className="border-l-4 border-l-amber-400">
              <CardContent className="p-4 flex flex-wrap items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-slate-800">{s.expand?.staff?.expand?.user?.name || 'Staff'}</p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-slate-600 mt-1">
                    <span>Basic: ?{s.basic_salary}</span>
                    <span className="text-red-500">Deductions: -?{s.deductions}</span>
                    <span className="text-red-500">Advance: -?{s.advance_deduction}</span>
                  </div>
                  <p className="text-lg font-bold text-green-600 mt-1">Net Payable: ?{s.net_payable}</p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={s.paid ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{s.paid ? '? Paid' : '? Pending'}</Badge>
                  {!s.paid && <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white" onClick={() => markPaid(s.id)}><Check className="w-4 h-4 mr-1" /> Mark Paid</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}